import { Element } from "@xmpp/xml"
import { Location } from "../reducers/locationsSlice"

class LocationParser {
  static parse(stanza: Element, nodeId: string): Location | undefined {
    const geoLoc = LocationParser.geolocChild(stanza, nodeId)

    if (!geoLoc) return undefined
    return this.parseGeoloc(geoLoc)
  }

  static parseGeoloc(geoLoc: Element): Location | undefined {
    const lat = geoLoc.getChildText("lat")
    const lon = geoLoc.getChildText("lon")

    if (!lat || !lon) return undefined

    return {
      lat,
      lon,
      bearing: geoLoc.getChildText("bearing") || undefined,
      country: geoLoc.getChildText("country") || undefined,
      countryCode: geoLoc.getChildText("countrycode") || undefined,
      locality: geoLoc.getChildText("locality") || undefined,
      street: geoLoc.getChildText("street") || undefined,
      postalCode: geoLoc.getChildText("postalcode") || undefined,
      formattedAddress: geoLoc.getChildText("dscription") || undefined, // typo in slixmpp library
      companyName: this.getCompanyName(geoLoc),
      odometer: this.getOdometer(geoLoc),
      fuelLevel: this.getFuelLevel(geoLoc),
      fuelLiters: this.getFuelLiters(geoLoc),
      temperatureMain: this.getTemperatureMain(geoLoc),
      temperatureAdditional: this.getTemperatureAdditional(geoLoc),
      stationarySince:
        geoLoc.getChildText("stationary_since", "urn:slickshift:geoloc") ||
        undefined,
      speed: this.getSpeed(geoLoc),
      timestamp: geoLoc.getChildText("timestamp") || undefined,
    }
  }

  static getSpeed(geoLoc: Element): number | undefined {
    const speed = parseFloat(geoLoc.getChildText("speed") ?? "")
    return Number.isFinite(speed) ? speed : undefined
  }

  static getOdometer(geoLoc: Element): number | undefined {
    const odometerText = geoLoc.getChildText(
      "odometer",
      "urn:slickshift:geoloc",
    )
    return odometerText ? parseFloat(odometerText) : undefined
  }

  static getFuelLevel(geoLoc: Element): number | undefined {
    const fuelLevelText = geoLoc.getChildText(
      "fuel_level",
      "urn:slickshift:geoloc",
    )
    return fuelLevelText ? parseFloat(fuelLevelText) : undefined
  }

  static getFuelLiters(geoLoc: Element): number | undefined {
    const fuelLitersText = geoLoc.getChildText(
      "fuel_liters",
      "urn:slickshift:geoloc",
    )
    return fuelLitersText ? parseFloat(fuelLitersText) : undefined
  }

  static getTemperatureMain(geoLoc: Element): number | undefined {
    return this.getTemperature(geoLoc, "main")
  }

  static getTemperatureAdditional(geoLoc: Element): number | undefined {
    return this.getTemperature(geoLoc, "additional")
  }

  static getCompanyName(geoLoc: Element): string | undefined {
    const parsedText = JSON.parse(geoLoc.getChildText("text") || "{}")
    return parsedText["company_name"] || undefined
  }

  static getTemperature(
    geoLoc: Element,
    childName: string,
  ): number | undefined {
    const temperatureElement = geoLoc.getChild(
      "temperature",
      "urn:slickshift:geoloc",
    )
    const temperatureText = temperatureElement?.getChildText(childName)
    return temperatureText ? parseFloat(temperatureText) : undefined
  }

  static geolocChild(stanza: Element, nodeId: string): Element | undefined {
    return this.nodeIdChild(stanza, nodeId)?.getChild(
      "geoloc",
      "http://jabber.org/protocol/geoloc",
    )
  }

  static retractChild(stanza: Element, nodeId: string): Element | undefined {
    const node = this.nodeIdChild(stanza, nodeId)

    return node?.name === "retract" ? node : undefined
  }

  static nodeIdChild(stanza: Element, nodeId: string): Element | undefined {
    const pubSubOrEventNode =
      stanza.getChild("pubsub", "http://jabber.org/protocol/pubsub") ||
      stanza.getChild("event", "http://jabber.org/protocol/pubsub#event")

    if (!pubSubOrEventNode) return undefined

    return pubSubOrEventNode
      .getChildByAttr("node", "http://jabber.org/protocol/geoloc")
      ?.getChildByAttr("id", nodeId)
  }
}

export default LocationParser
